import {
  Banner,
  BannerAppearanceColor,
  BannerActionButtonType,
  BannerCtaType,
  BannerLandingPage,
  BannerStatus,
  CreateBannerPayload,
  UpdateBannerPayload,
} from "../../models/banners";
import { API_ENDPOINTS } from "../../utils/api-service";
import { api, tenantContextHeader } from "./api";

const bannerTag = (id: string) => ({ type: "banners" as const, id });
const bannerListTag = (tenantId: string | null) => ({ type: "banners" as const, id: `LIST:${tenantId}` });

const unwrapApiData = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) return response;

  const record = response as Record<string, unknown>;
  return record.success === true && record.data !== undefined ? record.data : response;
};

const toNullableString = (value: unknown): string | null =>
  value === null || value === undefined || value === "" ? null : String(value);

const toNullableInt = (value: unknown): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

const mapLandingPage = (raw: unknown): BannerLandingPage | null => {
  if (typeof raw !== "object" || raw === null) return null;

  const row = raw as Record<string, unknown>;
  return {
    heroImageUrl: toNullableString(row.heroImageUrl),
    title: String(row.title ?? ""),
    body: toNullableString(row.body),
    importantMessage: toNullableString(row.importantMessage),
    appearanceColor: (row.appearanceColor ?? "#339966") as BannerAppearanceColor,
    actionButtonEnabled: row.actionButtonEnabled === true,
    actionButtonText: toNullableString(row.actionButtonText),
    actionButtonType: (toNullableString(row.actionButtonType) ??
      null) as BannerActionButtonType | null,
    actionButtonValue: toNullableString(row.actionButtonValue),
  };
};

const mapBanner = (raw: unknown): Banner => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;

  return {
    id: String(row.id ?? ""),
    name: String(row.name ?? ""),
    shortText: toNullableString(row.shortText),
    imageUrl: String(row.imageUrl ?? ""),
    imageWidth: toNullableInt(row.imageWidth),
    imageHeight: toNullableInt(row.imageHeight),
    imageSizeKb: toNullableInt(row.imageSizeKb),
    ctaType: (row.ctaType ?? "external_url") as BannerCtaType,
    ctaValue: toNullableString(row.ctaValue),
    status: (row.status ?? "inactive") as BannerStatus,
    sectionKey: String(row.sectionKey ?? "banners_carousel"),
    startDate: toNullableString(row.startDate),
    endDate: toNullableString(row.endDate),
    sortOrder: Number(row.sortOrder ?? 0),
    landingPage: mapLandingPage(row.landingPage),
    createdAt: toNullableString(row.createdAt),
    updatedAt: toNullableString(row.updatedAt),
  };
};

const mapBannerList = (response: unknown): Banner[] => {
  const data = unwrapApiData(response);
  if (Array.isArray(data)) return data.map(mapBanner);

  const record = (typeof data === "object" && data !== null ? data : {}) as Record<string, unknown>;
  return Array.isArray(record.banners) ? record.banners.map(mapBanner) : [];
};

export const bannersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBanners: build.query<Banner[], { tenantId: string | null }>({
      query: ({ tenantId }) => ({
        url: API_ENDPOINTS.BANNERS.BASE,
        headers: tenantContextHeader(tenantId),
      }),
      transformResponse: mapBannerList,
      providesTags: (result, _error, { tenantId }) => [
        ...(result ?? []).map((banner) => bannerTag(banner.id)),
        bannerListTag(tenantId),
      ],
    }),

    createBanner: build.mutation<Banner, { tenantId: string | null; body: CreateBannerPayload }>({
      query: ({ tenantId, body }) => ({
        url: API_ENDPOINTS.BANNERS.BASE,
        method: "POST",
        body,
        headers: tenantContextHeader(tenantId),
      }),
      transformResponse: (response: unknown) => mapBanner(unwrapApiData(response)),
      invalidatesTags: (_result, _error, { tenantId }) => [bannerListTag(tenantId)],
    }),

    updateBanner: build.mutation<
      Banner,
      { tenantId: string | null; id: string; body: UpdateBannerPayload }
    >({
      query: ({ tenantId, id, body }) => ({
        url: API_ENDPOINTS.BANNERS.BY_ID(id),
        method: "PATCH",
        body,
        headers: tenantContextHeader(tenantId),
      }),
      transformResponse: (response: unknown) => mapBanner(unwrapApiData(response)),
      invalidatesTags: (_result, _error, { id, tenantId }) => [bannerTag(id), bannerListTag(tenantId)],
    }),

    changeBannerStatus: build.mutation<
      Banner,
      { tenantId: string | null; id: string; status: BannerStatus }
    >({
      query: ({ tenantId, id, status }) => ({
        url: API_ENDPOINTS.BANNERS.STATUS(id),
        method: "PATCH",
        body: { status },
        headers: tenantContextHeader(tenantId),
      }),
      transformResponse: (response: unknown) => mapBanner(unwrapApiData(response)),
      async onQueryStarted({ tenantId }, { dispatch, queryFulfilled }) {
        try {
          const { data: savedBanner } = await queryFulfilled;
          dispatch(bannersApi.util.updateQueryData("getBanners", { tenantId }, (banners) => {
            const index = banners.findIndex((banner) => banner.id === savedBanner.id);
            if (index !== -1) banners[index] = savedBanner;
          }));
        } catch {
          return;
        }
      },
      invalidatesTags: (_result, _error, { id, tenantId }) => [bannerTag(id), bannerListTag(tenantId)],
    }),

    deleteBanner: build.mutation<void, { tenantId: string | null; id: string }>({
      query: ({ tenantId, id }) => ({
        url: API_ENDPOINTS.BANNERS.BY_ID(id),
        method: "DELETE",
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: (_result, _error, { id, tenantId }) => [bannerTag(id), bannerListTag(tenantId)],
    }),
  }),
});

export const {
  useGetBannersQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useChangeBannerStatusMutation,
  useDeleteBannerMutation,
} = bannersApi;
